import type { TableColumnsType } from "antd";
import { orderApi } from "api/order.api";
import { AppTable } from "components/antd-ui";
import { CodeCell, StatusCell } from "components/table";
import { useCallback, useMemo, useState } from "react";
import { Order } from "types/order";
import { formatMoney } from "views/Opportunity/opportunity.meta";
import { OrderDetailModal } from "views/Order/OrderDetailModal";
import { collectedAmountTone, orderStatusTrans } from "views/Order/order.meta";
import { useCustomerList } from "./useCustomerList";

// Tab "Đơn hàng": đơn đã chốt — hiển thị trạng thái triển khai
export const CustomerOrderTab = ({ customerId }: { customerId: number }) => {
  const [orderId, setOrderId] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const fetcher = useCallback(async (params: Record<string, unknown>) => {
    const res = await orderApi.findAll(params);
    return { items: (res.data.orders || []) as Order[], total: res.data.total || 0 };
  }, []);

  const { items, loading, pagination } = useCustomerList<Order>(customerId, fetcher, reloadKey);

  const columns = useMemo<TableColumnsType<Order>>(
    () => [
      { title: "Mã đơn", dataIndex: "code", key: "code", width: 120, render: (value) => <CodeCell value={value || "--"} /> },
      { title: "Mã hợp đồng", dataIndex: "contractCode", key: "contractCode", width: 140, render: (value) => value || "--" },
      {
        title: "Báo giá gốc",
        key: "opportunity",
        render: (_, record) =>
          record.opportunity ? `${record.opportunity.code || "--"} · ${record.opportunity.name || ""}` : "--",
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 160,
        render: (value) => <StatusCell value={value} trans={orderStatusTrans} />,
      },
      {
        title: "Giá trị đơn",
        key: "totalAmount",
        width: 140,
        align: "right",
        render: (_, record) => formatMoney(record.opportunity?.totalAmount || 0),
      },
      {
        title: "Đã thu",
        key: "collectedAmount",
        width: 140,
        align: "right",
        render: (_, record) => {
          const collected = record.collectedAmount || 0;
          const tone = collectedAmountTone(collected, record.opportunity?.totalAmount || 0);
          return <span className={`customerMoney customerMoney--${tone}`}>{formatMoney(collected)}</span>;
        },
      },
      {
        title: "Còn lại",
        key: "remain",
        width: 140,
        align: "right",
        render: (_, record) =>
          formatMoney(Math.max((record.opportunity?.totalAmount || 0) - (record.collectedAmount || 0), 0)),
      },
    ],
    [],
  );

  return (
    <>
      <AppTable<Order>
        className="customerTabTable"
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={pagination}
        locale={{ emptyText: "Chưa có đơn hàng" }}
        onRow={(record) => ({ onClick: () => setOrderId(record.id) })}
      />
      <OrderDetailModal
        orderId={orderId}
        open={orderId > 0}
        onClose={() => setOrderId(0)}
        onChanged={() => setReloadKey((value) => value + 1)}
      />
    </>
  );
};
